import { createRoot } from 'react-dom/client';
import { useCallback, useEffect, useState } from 'react';
import { HotspotOverlayApp } from './HotspotOverlayApp.jsx';
import { DISPLAY_FONT_APPLIED_EVENT } from '../displayFont.js';

/** @type {import('react-dom/client').Root | null} */
let root = null;

/** @type {((state: object) => void) | null} */
let setOverlayState = null;

const hiddenState = {
  visible: false,
  exiting: false,
  payload: null,
  animationKey: 0,
};

/**
 * @param {{ onExitComplete?: () => void }} props
 */
function HotspotOverlayRoot({ onExitComplete: onExitCompleteExternal }) {
  const [state, setState] = useState(hiddenState);
  const [fontEpoch, setFontEpoch] = useState(0);

  useEffect(() => {
    setOverlayState = setState;
    return () => {
      setOverlayState = null;
    };
  }, []);

  useEffect(() => {
    const onFontApplied = () => {
      setFontEpoch((epoch) => epoch + 1);
    };
    window.addEventListener(DISPLAY_FONT_APPLIED_EVENT, onFontApplied);
    return () => {
      window.removeEventListener(DISPLAY_FONT_APPLIED_EVENT, onFontApplied);
    };
  }, []);

  const handleExitComplete = useCallback(() => {
    setState(hiddenState);
    onExitCompleteExternal?.();
  }, [onExitCompleteExternal]);

  return (
    <HotspotOverlayApp
      visible={state.visible}
      exiting={state.exiting}
      animationKey={state.animationKey}
      fontEpoch={fontEpoch}
      payload={state.payload}
      onExitComplete={handleExitComplete}
    />
  );
}

/**
 * @param {HTMLElement} container
 * @param {{ onExitComplete?: () => void }} [options]
 */
export function mountHotspotOverlay(container, options = {}) {
  const { onExitComplete } = options;

  root = createRoot(container);
  root.render(<HotspotOverlayRoot onExitComplete={onExitComplete} />);

  return {
    /**
     * @param {{ markerName: string; driver: import('../drivers.js').Driver; description: string; cornerKey: string }} payload
     */
    show(payload) {
      setOverlayState?.((prev) => {
        const sameCorner =
          prev.visible &&
          !prev.exiting &&
          prev.payload?.cornerKey === payload.cornerKey;

        if (sameCorner) {
          return {
            visible: true,
            exiting: false,
            payload,
            animationKey: prev.animationKey,
          };
        }

        return {
          visible: true,
          exiting: false,
          payload,
          animationKey: (prev.animationKey ?? 0) + 1,
        };
      });
    },
    hide() {
      setOverlayState?.((prev) => {
        if (!prev.visible || !prev.payload) {
          return hiddenState;
        }
        return {
          ...prev,
          exiting: true,
        };
      });
    },
  };
}
